import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { IoMdWarning } from "react-icons/io";
import { Switch } from "@/components/Switch";
import { Alert } from "@/components/Alert";
import { Typography } from "@/components/Typography";
import { QREditorContext } from "@/contexts/QREditorContext";
import getQRReferenceHandler from "@/interface/api/getQRReference";
import { FormValidationMethods } from "@/components/Form/types";
import { SwitchWithInputProps } from "./types";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { getSiteInfo } from "@/sites";
import { useDebounce } from "@/hooks/utils/useDebounce";
import { useApi } from "@/hooks/api/useApi";
import { QR_REFERENCE_NOT_VALID, REFERENCE_EXITS } from "@/utils/qrEditorHelper";
import { InputAdapter } from "@/components/Input/adapter/InputAdapter";
import { SwitchWithInputContext } from "./context";

import "./styles.scss";

export const SwitchWithInputLib = ({
  label,
  checked,
  content,
  formFields,
  warning,
}: SwitchWithInputProps) => {
  const { t } = useTranslation("qr-editor-stats");

  const methods: FormValidationMethods = useFormContext();
  const { qrData, isQRfinished, setQrData } =
    useContext<QREditorContextProps<any>>(QREditorContext);

  const { qrCodeReference } = qrData;
  const { hasCustomReference } = qrData.data;

  const { domainName, scanDomain } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};
  const baseUrl = `https://www.${scanDomain || domainName}/p/`;

  const switchDefaultVal = hasCustomReference ? false : checked;

  const referenceInputValue =
    hasCustomReference && qrData.data.customReference
      ? qrData.data.customReference
      : qrCodeReference;

  const [switchVal, setSwitch] = useState(switchDefaultVal);
  const [inputVal, setInputVal] = useState(referenceInputValue || "");
  const [searchVal, setSearchVal] = useState("");

  const toggleSwitch = () => setSwitch((prevState) => !prevState);

  useEffect(() => {
    if (switchVal) {
      methods.unregister("customReference");
    } else {
      methods.register("customReference");
      document.getElementById("customReference")?.focus();
    }

    if (methods.formState.errors["customReference"]) {
      methods.clearErrors("customReference");
    }

    const copyQrData = { ...qrData.data };

    if (
      copyQrData.hasOwnProperty("hasCustomReference") &&
      copyQrData["hasCustomReference"] === true
    ) {
      delete copyQrData["hasCustomReference"];
      delete copyQrData["customeReference"];
    } else {
      copyQrData["hasCustomReference"] = true;
    }

    setQrData({
      ...qrData,
      data: {
        ...copyQrData,
      },
    });
  }, [switchVal]);

  const { request: requestQRReference, error } = useApi(getQRReferenceHandler.getQRReference);

  const handleQRReferenceRequest = async (e) => {
    if (e !== undefined) {
      await requestQRReference({ qrReference: e });
    }
  };

  useEffect(() => {
    if (error?.data?.code === QR_REFERENCE_NOT_VALID)
      methods.setError("customReference", {
        message: t("reference_pattern"),
      });

    if (error?.data?.code === REFERENCE_EXITS)
      methods.setError("customReference", {
        message: t("reference_exist"),
      });
  }, [error]);

  const debounceValue = useDebounce(searchVal, 300);

  useEffect(() => {
    searchVal !== "" && handleQRReferenceRequest(searchVal);
  }, [debounceValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputVal(value);
    setSearchVal(value);
  };

  return (
    <SwitchWithInputContext.Provider value={{ toggleSwitch, switchVal }}>
      <div className="switchWithInput">
        <Switch
          label={t(label)}
          content={t(content, { domain_name: domainName })}
        />
      </div>
      <>
        {!switchVal ? (
          <>
            <div className="Input__label-container">
              {formFields[0].label && (
                <label
                  data-testid="Input__label"
                  htmlFor={formFields[0].id}
                  className="Input__label"
                >
                  {formFields[0].label || ""}
                </label>
              )}
            </div>

            <InputAdapter
              disabled={switchVal}
              id={formFields[0].id}
              dataQA={formFields[0].data_qa}
              type="text"
              placeholder={"(Auto Create)"}
              ariaLabel="customReference"
              name="customReference"
              onChange={handleChange}
              error={methods.formState.errors["customReference"]}
              errorMessage={methods.formState.errors["customReference"]?.message as string}
              value={inputVal}
              prefix={
                <Typography
                  Tag="p"
                  size="s"
                  style={{ color: "#bbbbbb", marginTop: "3px", marginRight: "-10px" }}
                >
                  {baseUrl}
                </Typography>
              }
              rules={{
                required: {
                  value: true,
                  message: t("required_message"),
                },
                maxLength: {
                  value: 36,
                  message: t("reference_max_length"),
                },
              }}
            />
          </>
        ) : (
          <div className="input-mock single">
            {t(formFields[0].placeholder, { domain_name: scanDomain || domainName })}
          </div>
        )}

        {isQRfinished && warning && (
          <Alert
            icon={<IoMdWarning aria-label="warning-icon" />}
            text={t(warning)}
            type={"warning"}
            className="customReference__warning"
          />
        )}
      </>
    </SwitchWithInputContext.Provider>
  );
};
